import { create } from "zustand";
import { persist } from "zustand/middleware";
import { Cart, CartItem } from "@/types";

// Helper function to create clean cart items without circular references
function createCleanCartItem(item: CartItem): CartItem {
  // Create a safe item without problematic properties
  const safeItem: CartItem = {
    item_id: item.item_id,
    product_id: item.product_id,
    part_number: item.part_number,
    decal_number: item.decal_number,
    price: item.price,
    title: item.title,
    image: item.image,
    tier_id: item.tier_id,
    weight: item.weight || 0,
    moq: item.moq || 1,
    shipping_weight: item.shipping_weight || 0,
    shipping_width: item.shipping_width,
    shipping_height: item.shipping_height,
    shipping_length: item.shipping_length,
    width: item.width,
    height: item.height,
    length: item.length,
  };

  // Handle attachments safely
  if (item.attachments && Array.isArray(item.attachments)) {
    safeItem.attachments = item.attachments.map((att) => ({
      item_id: att.item_id,
      product_id: att.product_id,
      part_number: att.part_number,
      decal_number: att.decal_number,
      price: att.price || 0,
      title: att.title || "",
      image: att.image,
      tier_id: att.tier_id,
      quantity: att.quantity || 1,
      shipping_weight: att.shipping_weight || 0,
      shipping_width: att.shipping_width,
      shipping_height: att.shipping_height,
      shipping_length: att.shipping_length,
      weight: att.weight || 0,
      width: att.width,
      height: att.height,
      length: att.length,
      moq: att.moq || 1,
    }));
  }

  return safeItem;
}

// Helper function to prepare a cart for storage by cleaning all items
function prepareCartForStorage(cart: Cart): Cart {
  if (!cart) return cart;

  // Create a clean copy of the cart
  const cleanCart: Cart = {
    ...cart,
    // Set needsMigration for local carts with items
    needsMigration: cart.isServerCart
      ? cart.needsMigration !== undefined
        ? cart.needsMigration
        : false // Preserve existing needsMigration for server carts, default to false if undefined
      : cart.cart_items && cart.cart_items.length > 0, // Local carts with items need migration
    cart_items: Array.isArray(cart.cart_items)
      ? cart.cart_items.map((item) => createCleanCartItem(item))
      : [],
  };

  // Remove any snake_case fields that might have been added
  delete (cleanCart as unknown as Record<string, unknown>).needs_migration;

  return cleanCart;
}

// Helper function to prepare all carts for storage
function prepareCartsForStorage(carts: Cart[]): Cart[] {
  return carts.map(prepareCartForStorage);
}

interface CartStore {
  carts: Cart[];
  activeQuoteNumber: string | null;
  initialized: boolean;

  // Actions
  addCart: (cart: Cart) => void;
  removeCart: (quoteNumber: string) => void;
  setActiveCart: (quoteNumber: string) => void;
  updateCart: (quoteNumber: string, updates: Partial<Cart>) => void;
  setItems: (items: CartItem[]) => void;
  importCart: (cart: Cart) => void;
  clearAllCarts: () => void;
  updateCartsOrder: (quoteNumbers: string[]) => void;
  updateCartQuoteNumber: (
    oldQuoteNumber: string,
    newQuoteNumber: string
  ) => void;
  replaceCart: (oldQuoteNumber: string, newCart: Cart) => void;
  getCartsNeedingMigration: () => Cart[];
  clearMigrationFlag: (quoteNumber: string) => void;
  clearAllMigrationFlags: () => void;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      carts: [],
      activeQuoteNumber: null,
      initialized: false,

      addCart: (cart) => {
        set((state) => {
          // Check if cart already exists
          const exists = state.carts.some(
            (c) => c.quote_number === cart.quote_number
          );
          if (exists) return state;

          // Ensure the cart is clean before adding
          const cleanCart = prepareCartForStorage(cart);

          // Set migration flag for local carts with items
          if (
            !cleanCart.isServerCart &&
            cleanCart.cart_items &&
            cleanCart.cart_items.length > 0
          ) {
            cleanCart.needsMigration = true;
          }

          // Add new cart to the END of the array to maintain visual order with new tabs on the right
          return {
            carts: [...state.carts, cleanCart],
            activeQuoteNumber:
              state.activeQuoteNumber || cleanCart.quote_number,
          };
        });
      },

      removeCart: (quoteNumber) => {
        set((state) => {
          const filteredCarts = state.carts.filter(
            (c) => c.quote_number !== quoteNumber
          );

          // If we're removing the active cart, select a new one
          let newActiveQuoteNumber = state.activeQuoteNumber;
          if (
            state.activeQuoteNumber === quoteNumber &&
            filteredCarts.length > 0
          ) {
            newActiveQuoteNumber = filteredCarts[0].quote_number;
          } else if (filteredCarts.length === 0) {
            newActiveQuoteNumber = null;
          }

          // If this was the active cart, clean up localStorage
          if (state.activeQuoteNumber === quoteNumber) {
            try {
              localStorage.removeItem("active-cart-data");
            } catch (error) {
              console.error(
                "[CartStore] Failed to remove active-cart-data:",
                error
              );
            }
          }

          // Make sure all remaining carts are clean
          const cleanCarts = prepareCartsForStorage(filteredCarts);

          return {
            carts: cleanCarts,
            activeQuoteNumber: newActiveQuoteNumber,
          };
        });
      },

      setActiveCart: (quoteNumber) => {
        const { carts } = get();

        // Only set if cart exists
        if (carts.some((c) => c.quote_number === quoteNumber)) {
          set({ activeQuoteNumber: quoteNumber });

          // Also update active-cart-data in localStorage
          try {
            const activeCart = carts.find(
              (c) => c.quote_number === quoteNumber
            );
            if (activeCart) {
              const cleanCart = prepareCartForStorage(activeCart);
              localStorage.setItem(
                "active-cart-data",
                JSON.stringify(cleanCart)
              );
            }
          } catch (error) {
            console.error(
              "[CartStore] Failed to update active-cart-data:",
              error
            );
          }
        }
      },

      updateCart: (quoteNumber, updates) => {
        set((state) => {
          const updatedCarts = state.carts.map((cart) => {
            if (cart.quote_number === quoteNumber) {
              const updatedCart = { ...cart, ...updates };

              // If updating cart items, clean them to avoid circular references
              if (updates.cart_items) {
                updatedCart.cart_items = updates.cart_items.map((item) =>
                  createCleanCartItem(item)
                );

                // Set migration flag for local carts that now have items
                if (
                  !updatedCart.isServerCart &&
                  updatedCart.cart_items.length > 0
                ) {
                  updatedCart.needsMigration = true;
                } else if (updatedCart.cart_items.length === 0) {
                  // Clear migration flag if cart is now empty
                  updatedCart.needsMigration = false;
                }
              }

              // Update active-cart-data if this is the active cart
              if (state.activeQuoteNumber === quoteNumber) {
                try {
                  // Clean cart items before saving to localStorage
                  const cleanCart = prepareCartForStorage(updatedCart);

                  localStorage.setItem(
                    "active-cart-data",
                    JSON.stringify(cleanCart)
                  );
                } catch (error) {
                  console.error(
                    "[CartStore] Failed to update active-cart-data:",
                    error
                  );
                }
              }

              return updatedCart;
            }
            return cart;
          });

          return { carts: updatedCarts };
        });
      },

      setItems: (items) => {
        set((state) => {
          const { activeQuoteNumber, carts } = state;
          if (!activeQuoteNumber) return state;

          // Clean items before setting
          const cleanItems = items.map(createCleanCartItem);

          const updatedCarts = carts.map((cart) => {
            if (cart.quote_number === activeQuoteNumber) {
              const updatedCart = { ...cart, cart_items: cleanItems };

              // Set migration flag for local carts that now have items
              if (!updatedCart.isServerCart && cleanItems.length > 0) {
                updatedCart.needsMigration = true;
              } else if (cleanItems.length === 0) {
                // Clear migration flag if cart is now empty
                updatedCart.needsMigration = false;
              }

              // Update active-cart-data
              try {
                // Clean the entire cart before storing
                const cleanCart = prepareCartForStorage(updatedCart);

                localStorage.setItem(
                  "active-cart-data",
                  JSON.stringify(cleanCart)
                );
              } catch (error) {
                console.error(
                  "[CartStore] Failed to update active-cart-data:",
                  error
                );
              }

              return updatedCart;
            }
            return cart;
          });

          return { carts: updatedCarts };
        });
      },

      importCart: (cart) => {
        set((state) => {
          // Clean the cart before importing
          const cleanCart = prepareCartForStorage(cart);

          // Set migration flag for local carts with items
          if (
            !cleanCart.isServerCart &&
            cleanCart.cart_items &&
            cleanCart.cart_items.length > 0
          ) {
            cleanCart.needsMigration = true;
          }

          // Check if cart already exists
          const existingCartIndex = state.carts.findIndex(
            (c) => c.quote_number === cleanCart.quote_number
          );

          if (existingCartIndex >= 0) {
            // Update existing cart
            const updatedCarts = [...state.carts];
            updatedCarts[existingCartIndex] = cleanCart;

            // Update active-cart-data if this is the active cart
            if (state.activeQuoteNumber === cleanCart.quote_number) {
              try {
                localStorage.setItem(
                  "active-cart-data",
                  JSON.stringify(cleanCart)
                );
              } catch (error) {
                console.error(
                  "[CartStore] Failed to update active-cart-data:",
                  error
                );
              }
            }

            return { carts: updatedCarts };
          } else {
            // Add new cart
            return {
              carts: [...state.carts, cleanCart],
              activeQuoteNumber:
                state.activeQuoteNumber || cleanCart.quote_number,
            };
          }
        });
      },

      clearAllCarts: () => {
        set({ carts: [], activeQuoteNumber: null });

        // Clear active-cart-data as well
        try {
          localStorage.removeItem("active-cart-data");
        } catch (error) {
          console.error(
            "[CartStore] Failed to remove active-cart-data:",
            error
          );
        }
      },

      updateCartsOrder: (quoteNumbers) => {
        set((state) => {
          // Create a map of existing carts for fast lookup
          const cartMap = new Map();
          state.carts.forEach((cart) => {
            cartMap.set(cart.quote_number, cart);
          });

          // Create a new array of carts in the specified order
          const orderedCarts: Cart[] = [];

          // First add carts in the requested order
          quoteNumbers.forEach((quoteNumber) => {
            const cart = cartMap.get(quoteNumber);
            if (cart) {
              orderedCarts.push(cart);
              cartMap.delete(quoteNumber); // Remove to prevent duplicates
            }
          });

          // Then add any remaining carts that weren't in the quoteNumbers array
          cartMap.forEach((cart) => {
            orderedCarts.push(cart);
          });

          // Clean all carts before storage
          const cleanOrderedCarts = prepareCartsForStorage(orderedCarts);

          return { carts: cleanOrderedCarts };
        });
      },

      updateCartQuoteNumber: (oldQuoteNumber, newQuoteNumber) => {
        set((state) => {
          const updatedCarts = state.carts.map((cart) => {
            if (cart.quote_number === oldQuoteNumber) {
              return { ...cart, quote_number: newQuoteNumber };
            }
            return cart;
          });

          // Also update activeQuoteNumber if needed
          const newActiveQuoteNumber =
            state.activeQuoteNumber === oldQuoteNumber
              ? newQuoteNumber
              : state.activeQuoteNumber;

          // Clean all carts before storage
          const cleanCarts = prepareCartsForStorage(updatedCarts);

          return {
            carts: cleanCarts,
            activeQuoteNumber: newActiveQuoteNumber,
          };
        });
      },

      // Replace a cart with a new cart while maintaining its position in the array
      // This is useful when converting a local cart to a server cart and you want
      // to preserve the tab order in the UI
      replaceCart: (oldQuoteNumber, newCart) => {
        set((state) => {
          // Clean the new cart before replacing
          const cleanNewCart = prepareCartForStorage(newCart);

          const updatedCarts = state.carts.map((cart) => {
            if (cart.quote_number === oldQuoteNumber) {
              return cleanNewCart;
            }
            return cart;
          });

          // Also update activeQuoteNumber if needed
          const newActiveQuoteNumber =
            state.activeQuoteNumber === oldQuoteNumber
              ? cleanNewCart.quote_number
              : state.activeQuoteNumber;

          // Update active-cart-data if this is the active cart
          if (state.activeQuoteNumber === oldQuoteNumber) {
            try {
              localStorage.setItem(
                "active-cart-data",
                JSON.stringify(cleanNewCart)
              );
            } catch (error) {
              console.error(
                "[CartStore] Failed to update active-cart-data:",
                error
              );
            }
          }

          // Clean all carts before storage
          const cleanCarts = prepareCartsForStorage(updatedCarts);

          return {
            carts: cleanCarts,
            activeQuoteNumber: newActiveQuoteNumber,
          };
        });
      },

      getCartsNeedingMigration: () => {
        const { carts } = get();
        return carts.filter((cart) => cart.needsMigration);
      },

      clearMigrationFlag: (quoteNumber) => {
        set((state) => {
          const updatedCarts = state.carts.map((cart) => {
            if (cart.quote_number === quoteNumber) {
              return { ...cart, needsMigration: false };
            }
            return cart;
          });

          // Clean all carts before storage
          const cleanCarts = prepareCartsForStorage(updatedCarts);

          return { carts: cleanCarts };
        });
      },

      clearAllMigrationFlags: () => {
        set((state) => {
          const updatedCarts = state.carts.map((cart) => {
            return { ...cart, needsMigration: false };
          });

          return { carts: updatedCarts };
        });
      },
    }),
    {
      name: "user-carts",
      partialize: (state) => {
        // Clean all carts before persisting state
        const cleanCarts = prepareCartsForStorage(state.carts);

        return {
          carts: cleanCarts,
          activeQuoteNumber: state.activeQuoteNumber,
          initialized: true,
        };
      },
    }
  )
);
